import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { vehicleService, VehicleDTO } from '../../../../services/vehicleService';
import { CirclePlus, Eye, RefreshCw, SquarePen, Car } from 'lucide-react';
import '../CarRentalPage.css';

const VehicleListPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<VehicleDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unwrap = (res: any): VehicleDTO[] => {
    const d = res?.data?.data;
    return Array.isArray(d) ? d : (d?.data ?? []);
  };

  const load = async (spinner: 'loading' | 'refreshing' = 'loading') => {
    spinner === 'loading' ? setLoading(true) : setRefreshing(true);
    setError(null);
    try {
      const res = await vehicleService.getAll();
      setItems(unwrap(res));
    } catch {
      setError('Failed to load vehicles');
    } finally {
      spinner === 'loading' ? setLoading(false) : setRefreshing(false);
    }
  };

  useEffect(() => { load('loading'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const availableCount = items.filter((i) => i.status === 'Available').length;

  const columns: GridColumn[] = [
    { key: 'vehicleNumber', label: 'Vehicle #', width: '120px' },
    { key: 'make', label: 'Make', render: (v, item) => <strong>{v} {item.model}</strong> },
    { key: 'registrationNumber', label: 'Registration', width: '140px' },
    { key: 'vehicleCategoryName', label: 'Category', width: '140px', render: (v) => v || '—' },
    { key: 'rentalRate', label: 'Rate/Day', width: '100px', render: (v) => `$${Number(v).toFixed(2)}` },
    {
      key: 'status', label: 'Status', width: '130px',
      render: (v) => <span className={`ims-badge ims-badge-${String(v).toLowerCase()}`}>{v}</span>,
    },
  ];

  const actions: GridAction[] = [
    { icon: <Eye size={16} strokeWidth={1.75} />, onClick: (item) => navigate(`/car-rental/vehicles/${item.id}/edit`), className: 'grid-action-btn-view', title: 'View' },
    { icon: <SquarePen size={16} strokeWidth={1.75} />, onClick: (item) => navigate(`/car-rental/vehicles/${item.id}/edit`), className: 'grid-action-btn-edit', title: 'Edit' },
  ];

  return (
    <Layout>
      <div className="ims-page car-rental-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">Vehicles</h1>
            <p className="ims-list-subtitle">Manage the rental fleet</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={() => load('refreshing')} disabled={refreshing}>
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="btn-new" onClick={() => navigate('/car-rental/vehicles/create')}>
              <CirclePlus size={16} strokeWidth={1.75} /> New Vehicle
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-fleet"><Car size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">Total Fleet</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-avail"><Car size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{availableCount}</span>
              <span className="ims-stat-label">Available Now</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid data={items} columns={columns} actions={actions} loading={loading} pageSize={10} searchPlaceholder="Search vehicles..." />
        </div>
      </div>
    </Layout>
  );
};

export default VehicleListPage;
